import { withAuthenticationRequired } from '@auth0/auth0-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';

import { clientHome, home, MainRoutes, MobileRoutes } from './MainRoutes';
import { hasDesktopOptIn, MOBILE_VIEWPORT_MAX_PX } from './mobileRedirect';
import { CustomPermission } from '../@types';
import { AppShellSkeleton } from '../components/molecules/AppShellSkeleton';
import { MainMenuItem } from '../components/molecules/Drawer/Drawer.props';
import { menuData } from '../configs/menu';
import { permittedLayouts, permittedRoutes, RouteConfig } from '../configs/permissions';
import { useGetUserSystemPermissions } from '../queries/user-query';
import { useGetCurrentUserVisibility } from '../queries/visibilityRules-query';
import { isMobileDevice } from '../utils/is-mobile-device';

const ThemeRoutes = () => {
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const navigate = useNavigate();
  const location = useLocation();
  let layoutes: MainMenuItem[] = [];

  // Redirect mobile-class devices to /mobile/services any time they land on a
  // non-mobile route, unless they explicitly opted into the desktop UI via the
  // "Desktop view" button. Re-runs on every route change so /home links from
  // menus, bookmarks, and Auth0 callbacks all funnel mobile users back.
  useEffect(() => {
    const isMobileUA = isMobileDevice();
    const isNarrowViewport = window.innerWidth < MOBILE_VIEWPORT_MAX_PX;
    const optedIntoDesktop = hasDesktopOptIn();
    const isAlreadyOnMobile = location.pathname.startsWith('/mobile');

    if (isMobileUA && isNarrowViewport && !optedIntoDesktop && !isAlreadyOnMobile) {
      navigate('/mobile/services', { replace: true });
    }
  }, [location.pathname, navigate]);

  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const getCurrentUserVisibility = useGetCurrentUserVisibility();
  const isClientCustomer = !!getCurrentUserVisibility.data?.hasClientCustomer;

  useEffect(() => {
    if (getUserPermissionsQuery.isSuccess && getUserPermissionsQuery.data) {
      setPermissions(getUserPermissionsQuery.data);
    }
  }, [getUserPermissionsQuery.isLoading, getUserPermissionsQuery.isSuccess]);

  // The Home menu entry (in `permittedLayouts`) and the `/` route element must
  // stay in lockstep — otherwise a client-customer without /services access
  // would have Home hidden from the nav while `/` still rendered the Home
  // dashboard. `useClientHome` is the single source of truth for both.
  // `/services` is in the route table iff the user has any `service.*`
  // permission (the `services` parent menu carries `url: '/services'`), so a
  // permission-level check is equivalent to scanning the filtered routes and
  // avoids a chicken-and-egg with `permittedLayouts`.
  const hasServicePermission =
    permissions?.some((p) => {
      const code = typeof p === 'string' ? p : p.code;
      return code === 'service' || code.startsWith('service.');
    }) ?? false;
  const useClientHome = isClientCustomer && hasServicePermission;

  if (permissions) {
    layoutes = permittedLayouts(menuData, permissions, { isClientCustomer: useClientHome });
  }

  const filteredRoutes: RouteConfig = permittedRoutes(layoutes, MainRoutes);
  filteredRoutes.children.push(useClientHome ? clientHome : home);

  const routes = useRoutes([filteredRoutes, MobileRoutes]);

  // Hold the route tree until the visibility query settles too — otherwise a
  // client-customer first paint would briefly mount Home before swapping to
  // the /services redirect, flashing the wrong dashboard. Mobile routes don't
  // depend on the Home/Services decision, so let them render immediately even
  // if visibility is still loading. We deliberately fall through on error
  // (useClientHome becomes false → Home) to match the existing pattern in
  // CreateService/AddService and avoid an indefinite skeleton if
  // /getVisibilityRuleAccess is down; a client-customer who hits this case
  // can navigate to /services manually until the next retry.
  const isMobilePath = location.pathname.startsWith('/mobile');
  if (!permissions || (!isMobilePath && getCurrentUserVisibility.isLoading)) {
    return <AppShellSkeleton />;
  }

  return routes;
};

export default withAuthenticationRequired(ThemeRoutes);
